<template>
    <div class="PaymentSuccessWithDeliveryModes">
        <router-link
            v-if="validationAnimationFinished"
            :to="{ name: 'purchases' }"
            class="PaymentSuccessWithDeliveryModes-linkToOrders"
        >
            {{ $t('payment:success:go_to_orders') }}
        </router-link>
        <div class="PaymentSuccessWithDeliveryModes-validationContainer">
            <icomoon-icon
                :name="'Check'"
                class="PaymentSuccessWithDeliveryModes-icon"
            />
            <h1 class="PaymentSuccessWithDeliveryModes-title h1">
                {{ title }}
            </h1>
        </div>
        <template v-if="validationAnimationFinished">
            <payment-regular-card
                v-if="cartStatus === 'VALIDATION_ERROR'"
                v-bind="getValidationError"
            />
            <div
                v-else
                :class="{ 'PaymentSuccessWithDeliveryModes-cards--monoDeliveryMode' : Object.keys(getDeliveryModes).length === 1 }"
                class="PaymentSuccessWithDeliveryModes-cards"
            >
                <DeliveryCard
                    v-for="(deliveryMode, index) in getDeliveryModes"
                    :key="index"
                    :context="getContext"
                    :delivery-mode
                    :is-background-under-illustration-displayed="isBackgroundUnderIllustrationDisplayed"
                    :network-id="getNetworkId"
                    class="PaymentSuccessWithDeliveryModes-card"
                    @action-triggered="onActionTriggered"
                >
                    <template
                        v-if="getShowRecap"
                        #recap
                    >
                        <div class="PaymentSuccessWithDeliveryModes-recap">
                            <cart-basket
                                v-for="(basket, basketIndex) in deliveryMode.baskets"
                                :key="basketIndex"
                                :basket
                                :is-price-displayed="false"
                            />
                        </div>
                    </template>
                </DeliveryCard>
            </div>
            <div class="PaymentSuccessWithDeliveryModes-buttonsArea">
                <v-btn
                    :to="{ name: 'catalog' }"
                    class="PaymentSuccessWithDeliveryModes-goToCatalogButton"
                    color="primary"
                    size="large"
                    variant="text"
                >
                    {{ $t('button:back_to_catalog') }}
                </v-btn>
            </div>
        </template>
        <support-reader-modal
            v-model="showSupportReaderModal"
            :operationType="'WRITE'"
            @operationFinished="operationFinished"
        />
    </div>
</template>

<script setup>
import CartBasket from '@/StoreWeb/components/cart/CartBasket.vue'
import IcomoonIcon from '@/StoreWeb/components/common/IcomoonIcon'
import SupportReaderModal from '@/StoreWeb/components/miscellaneous/conduent-support-reader/SupportReaderModal'
import PaymentRegularCard from '@/StoreWeb/components/payment/PaymentRegularCard'
import { useLogin } from '@/StoreWeb/js/composables/login-utils'
import * as applicationSessionActionTypes from '@/StoreWeb/store/modules/application-session/action-types'
import * as externalActionTypes from '@/StoreWeb/store/modules/application-session/external-action-types'
import * as internalActionTypes from '@/StoreWeb/store/modules/application-session/internal-action-types'
import * as applicationSessionMutationTypes from '@/StoreWeb/store/modules/application-session/mutation-types'
import config from 'config'
import { isEmpty } from 'global-utils'
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'

const props = defineProps({
    baskets: {
        type: Array,
        required: true
    },
    cartStatus: {
        type: String,
        required: true
    },
    id: {
        type: Number,
        required: true
    },
    title: {
        type: String,
        required: true
    }
})

const router = useRouter()
const store = useStore()
const { isAuthenticated } = useLogin()

const deliveryModesCorrelation = config?.features?.finalized_order?.delivery_modes_correlation || {}
const deliveryModesOrder = config?.features?.finalized_order?.delivery_modes_order || []

const showSupportReaderModal = ref(false)
const validationAnimationFinished = ref(false)

const applicationSession = computed(() => store.state.applicationSessionModule.applicationSession)
const getCardReaderDeliverableFareMedias = computed(() => {
    return props.baskets
        .filter(({ basket }) => ['CARDREADER_MANDATORY', 'DISTRIBUTION_CARDREADER', 'DISTRIBUTION_MOBILE', 'REGULARIZATION'].includes(basket.deliveryMode))
        .map(({ basket }) => ({
            fareMediaId: basket.fareMediaId,
            label: basket.getLabel(),
            providerId: basket.providerId,
            subLabel: basket.getSubLabel()
        }))
        // Remove fareMedias duplicate entries
        .filter((fareMedia, index, filteredBaskets) => filteredBaskets.findIndex(fm => fm.fareMediaId === fareMedia.fareMediaId && fm.providerId === fareMedia.providerId) === index)
})
const getContext = computed(() => ({
    isAuthenticated: isAuthenticated.value,
    isMobileApp: isMobileApp.value,
    isMobileOrTablet: isMobileOrTablet.value,
    isNfcSupported: isNfcSupported.value
}))
const getDeliveryModes = computed(() => {
    const basketsByDeliveryMode = props.baskets.reduce((acc, { basket }) => {
        const deliveryMode = getBasketDeliveryMode(basket)

        if (deliveryMode) {
            const deliveryModeKey = deliveryModesCorrelation[deliveryMode] || deliveryMode

            if (!acc[deliveryModeKey]) {
                const actions = config?.features?.finalized_order?.actions_by_delivery_mode?.[deliveryModeKey] || {}

                acc[deliveryModeKey] = {
                    actions,
                    baskets: [],
                    id: deliveryModeKey
                }
            }

            addCardReaderDeliverableFareMedias(acc, deliveryModeKey, deliveryMode)
            addOrderCardReaderLink(acc, deliveryModeKey, basket)

            acc[deliveryModeKey].baskets.push(basket)
        }

        return acc
    }, {})

    return reorderBasketsByDeliveryMode(groupSimilarDeliveryModes(basketsByDeliveryMode))
})
const getNetworkId = computed(() => {
    return process.env.VUE_APP_NETWORK_ID
})
const getShowRecap = computed(() => config?.features?.finalized_order?.template === 'group_by_delivery_mode_with_recap')
const isBackgroundUnderIllustrationDisplayed = computed(() => config?.features?.finalized_order?.display_background_under_illustration)
const getValidationError = computed(() => {
    return {
        description: 'payment:success:VALIDATION_ERROR:description',
        icon: 'VALIDATION_ERROR',
        id: props.id,
        title: 'payment:success:VALIDATION_ERROR:title',
        withButton: false
    }
})
const isMobileApp = computed(() => store.state.isMobileApp)
const isMobileOrTablet = computed(() => store.state.isMobileOrTablet)
const isNfcSupported = computed(() => applicationSession.value?.data?.isNfcSupported)

function addCardReaderDeliverableFareMedias (deliveryModes, deliveryModeKey, deliveryMode) {
    if (['CARDREADER_MANDATORY', 'DISTRIBUTION_CARDREADER', 'DISTRIBUTION_MOBILE', 'REGULARIZATION'].includes(deliveryMode)) {
        deliveryModes[deliveryModeKey].cardReaderDeliverableFareMedias = getCardReaderDeliverableFareMedias.value
    }
}

function addOrderCardReaderLink (deliveryModes, deliveryModeKey, basket) {
    if (!(isMobileApp.value || isMobileOrTablet.value) &&
        ['CARDREADER_MANDATORY', 'DISTRIBUTION_CARDREADER', 'REGULARIZATION'].includes(basket.deliveryMode)) {
        const providerConfig = config.providers.find(provider => provider.id === basket.providerId)

        if (!isEmpty(providerConfig?.order_card_reader_link)) {
            if (providerConfig.order_card_reader_link?.productId) {
                deliveryModes[deliveryModeKey].orderCardReaderLink = {
                    name: 'product',
                    params: { id: providerConfig.order_card_reader_link.productId }
                }
            }

            if (providerConfig.order_card_reader_link?.url) {
                deliveryModes[deliveryModeKey].orderCardReaderLink = providerConfig.order_card_reader_link.url
            }
        }
    }
}

function convertDeliveryMode (deliveryMode) {
    if (!isMobileApp.value) {
        return deliveryMode
    }

    if (
        isMobileApp.value &&
        isNfcSupported.value &&
        ['CARDREADER_MANDATORY', 'DISTRIBUTION_CARDREADER'].includes(deliveryMode)
    ) {
        return 'DISTRIBUTION_MOBILE'
    }

    return deliveryMode
}

// This function will be removed in the future, once the deliveryMode property is always set in the basket object
// regardless its products deliveryMode
function getBasketDeliveryMode (basket) {
    if (basket?.type === 'REGULARIZATION') {
        return 'REGULARIZATION'
    }

    if (basket?.deliveryMode) {
        return convertDeliveryMode(basket.deliveryMode)
    }

    if (basket?.products?.length && basket.products[0]?.item?.deliveryMode) {
        return convertDeliveryMode(basket.products[0].item.deliveryMode)
    }

    return 'UNKNOWN_DELIVERY_MODE'
}

function groupSimilarDeliveryModes (basketsByDeliveryMode) {
    Object.keys(deliveryModesCorrelation).forEach(similarDeliveryMode => {
        if (basketsByDeliveryMode[similarDeliveryMode]) {
            mergeDeliveryModes(basketsByDeliveryMode, similarDeliveryMode)
        }
    })

    return basketsByDeliveryMode
}

function mergeDeliveryModes (basketsByDeliveryMode, similarDeliveryMode) {
    const targetMode = deliveryModesCorrelation[similarDeliveryMode]
    const sourceBaskets = basketsByDeliveryMode[similarDeliveryMode]

    if (!basketsByDeliveryMode[targetMode]) {
        basketsByDeliveryMode[targetMode] = sourceBaskets
    } else {
        basketsByDeliveryMode[targetMode].baskets.push(...sourceBaskets.baskets)
    }

    delete basketsByDeliveryMode[similarDeliveryMode]
}

function reorderBasketsByDeliveryMode (basketsByDeliveryMode) {
    return Object.fromEntries(
        Object.keys(basketsByDeliveryMode)
            .sort((a, b) => deliveryModesOrder.indexOf(a) - deliveryModesOrder.indexOf(b))
            .map(key => [key, basketsByDeliveryMode[key]])
    )
}

function operationFinished () {
    showSupportReaderModal.value = false
}

function onActionTriggered (action) {
    switch (action.name) {
        case 'GO_TO_DIRECT_DEBITS':
            router.push({ name: 'directDebits' })
            return
        case 'GO_TO_WALLET':
            router.push({ name: 'tickets' })
            return
        case 'USE_CARD_READER':
            useCardReader()
            return
        case 'USE_NFC':
            useCardMobileReaderSDK(action.deliverableFareMedias)
            return
        default:
            router.push({ name: 'purchases' })
    }
}

function useCardReader () {
    showSupportReaderModal.value = true
}

async function useCardMobileReaderSDK (deliverableFareMedias) {
    applicationSession.value.data.waitingForReloadingFareMedias = deliverableFareMedias
    const baseUrl = `${window.location.origin}/internalLinks/${internalActionTypes.GO_TO_CATALOG}?sessionId=${applicationSession.value.id}&initialRequest=${externalActionTypes.RELOAD_FARE_MEDIA_THROUGH_NFC}`
    applicationSession.value.sessionContext = {
        currentStep: baseUrl,
        resumeStepSuccess: `${baseUrl}&status=success`,
        resumeStepError: `${baseUrl}&status=error`
    }
    store.commit(applicationSessionMutationTypes.SET_APPLICATION_SESSION, applicationSession.value)
    await store.dispatch(applicationSessionActionTypes.UPDATE_APPLICATION_SESSION)

    const externalLink = router.resolve({
        name: 'externalLinks',
        params: { action: externalActionTypes.RELOAD_FARE_MEDIA_THROUGH_NFC },
        query: { sessionId: applicationSession.value.id }
    })

    if (!isEmpty(externalLink)) {
        window.location.href = window.location.origin + externalLink.href
    }
}

onMounted(() => {
    setTimeout(() => {
        // Use this instead of css to avoid glitch
        validationAnimationFinished.value = true
    }, 3000)
})
</script>

<style lang='scss' scoped>
@import 'globalScss';

.PaymentSuccessWithDeliveryModes {
    display: flex;
    flex-direction: column;
    gap: $s10;
    position: relative;

    &-linkToOrders {
        position: absolute;
        z-index: 1;
        top: 0;
        left: $s4;
        font-weight: $fontWeight-defaultBold;
        color: $color-brandPrimary;
        transition: color .3s;

        &:hover,
        &:active {
            color: $color-brandPrimaryDark;
        }

        @media #{map-get($display-breakpoints, 'md-and-up')} {
            right: $s4;
            left: auto;
        }
    }

    &-validationContainer {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-bottom: $s8;
        opacity: 0;
        transform: scale(2.2) translateY(calc(100px + $s8));
        animation: animation-validation 3s forwards;
    }

    &-icon {
        font-size: 64px;
        color: $color-brandSecondary;
    }

    &-title {
        margin: 0;
    }

    &-validationError {
        margin: 0 $s5;
    }

    &-cards {
        display: grid;
        grid-template-columns: 1fr;
        gap: $s4;
        border-radius: $borderRadius-xlarge;
        background: $backgroundColor-greyGradient;
        opacity: 0;
        transform: translateY(50px);
        animation: animation-cards 1s forwards;

        @media #{map-get($display-breakpoints, 'md-and-up')} {
            grid-template-columns: repeat(2, 1fr);
            gap: $s6;
            border-radius: 0;
            background: $color-white;

            &--monoDeliveryMode {
                grid-template-columns: 1fr;
            }
        }
    }

    &-card {
        @media #{map-get($display-breakpoints, 'md-and-up')} {
            border-radius: $borderRadius-xlarge;
            background: $backgroundColor-greyGradient;
        }
    }

    &-recap {
        align-self: center;
        width: 100%;
        max-width: 500px;
        padding: $s4;
        border-radius: $borderRadius-xlarge;
        background: $color-white;

        .CartBasket {
            margin-bottom: $s4;

            &:last-child {
                margin-bottom: 0;
            }
        }
    }

    &-buttonsArea {
        padding: 0 $s5;
    }

    &-goToCatalogButton {
        font-weight: $fontWeight-defaultBold;
    }
}

@keyframes animation-validation {
    0% {
        opacity: 0;
        transform: scale(2.2) translateY(calc(100px + $s8));
    }

    50% {
        opacity: 1;
        transform: scale(1) translateY(calc(100px + $s8));
    }

    100% {
        opacity: 1;
        transform: scale(1) translateY($s8);
    }
}

@keyframes animation-cards {
    0% {
        opacity: 0;
        transform: translateY(50px);
    }

    100% {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
